import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  useAuthSettings,
  useCreateSession,
  useRateQuest,
  useMyRoom,
  useQuest,
  useQuestLeaderboard,
  useStartRoomGame,
  useTags,
} from "../api/queries";
import { FavoriteButton } from "../components/FavoriteButton";
import { FeedbackDialog } from "../components/FeedbackDialog";
import { StarRating } from "../components/StarRating";
import { PlayerLink } from "../components/PlayerLink";
import { ErrorState, Skeleton, Tag } from "../components/ui";
import { formatClock, formatDate, formatScore } from "../lib/format";
import { play, throttled } from "../lib/sound";
import { tagIndex, tagName } from "../lib/tags";
import { useAuthStore } from "../stores/authStore";
import { usePageMeta } from "../lib/usePageMeta";

// Ползунки шлют события десятками в секунду: без ограничения они звучали бы
// очередью. Функция общая на модуль — двух ползунков одновременно не тянут.
const clickSlider = throttled("year", 120);

export function QuestDetailPage() {
  const { t } = useTranslation();
  const tagsIndex = tagIndex(useTags().data?.tags);
  const { questId } = useParams<{ questId: string }>();
  const navigate = useNavigate();
  const status = useAuthStore((state) => state.status);
  const user = useAuthStore((state) => state.user);

  const quest = useQuest(questId);
  const leaderboard = useQuestLeaderboard(questId, status === "authenticated");
  const createSession = useCreateSession();
  const room = useMyRoom(status === "authenticated").data;
  const startRoomGame = useStartRoomGame();
  const rate = useRateQuest();

  // Хозяин комнаты запускает партию отсюда: комната собрана заранее, ей
  // остаётся выбрать, во что играть.
  const isRoomHost = room?.members?.some(
    (member) => member.isHost && member.user?.id === user?.id,
  );

  const [seconds, setRoundSeconds] = useState<number | null>(null);
  const [reporting, setReporting] = useState(false);
  // Ноль — «сколько получится»: сервер сам возьмёт весь квест до потолка.
  // Пока настройки не доехали, выбирать не из чего, и это как раз тот случай.
  const [roundCount, setRoundCount] = useState(0);
  const settings = useAuthSettings().data;
  // Название и описание квеста — то, что человек ищет; без них все квесты
  // выглядели бы для поисковика одной страницей.
  usePageMeta({
    title: quest.data?.title,
    description:
      quest.data?.description || t("seo.quest", { count: quest.data?.roundCount ?? 0 }),
    noindex: quest.isError,
  });

  if (quest.isPending) return <Skeleton className="h-96" />;
  if (quest.isError || !quest.data) {
    return <ErrorState message={t("questPage.notFound")} onRetry={() => void quest.refetch()} />;
  }

  // Кадров в квесте — сколько собрал автор; раундов в партии — сколько из них
  // разыгрывается. Второе никогда не больше первого и не больше потолка.
  const rounds = quest.data.roundCount ?? 0;
  const maxRounds = Math.min(rounds, settings?.maxSessionRounds ?? rounds);
  const playRounds = roundCount > 0 && roundCount <= maxRounds ? roundCount : maxRounds;

  // Границы таймера — с сервера: он же и обрежет выбранное, если разойдётся.
  // Пока настройки не доехали, ползунок стоит там, где его поставит сервер.
  const maxSeconds = settings?.maxRoundSeconds ?? 600;
  const roundSeconds = seconds ?? settings?.defaultRoundSeconds ?? 120;

  const startSolo = () => {
    if (status !== "authenticated") {
      navigate("/login", { state: { from: `/quests/${questId}` } });
      return;
    }

    // Через mutate с колбэками, а не mutateAsync: отказ сервера (например,
    // «подтвердите адрес») иначе всплывал бы необработанным исключением.
    // Текст ошибки показывается ниже по состоянию мутации.
    play("start");
    createSession.mutate(
      {
        questId: questId!,
        mode: "GAME_MODE_SOLO",
        roundCount: playRounds,
        roundDurationSeconds: roundSeconds,
      },
      { onSuccess: (session) => navigate(`/play/${session.id}`) },
    );
  };

  return (
    <div className="grid gap-4 sm:gap-6 lg:grid-cols-[1.5fr_1fr]">
      <div className="space-y-4 sm:space-y-5">
        {quest.data.coverImageUrl && (
          <img
            src={quest.data.coverImageUrl}
            alt=""
            className="aspect-[16/9] w-full rounded-2xl border border-ink-800 object-cover"
          />
        )}

        <div>
          {quest.data.type === "QUEST_TYPE_DAILY" && (
            <span className="chip border-amber-400/40 text-amber-300">{t("quest.daily")}</span>
          )}
          {quest.data.type === "QUEST_TYPE_WORLD" && (
            <span className="chip border-amber-400/40 text-amber-300">{t("quest.world")}</span>
          )}
          <h1 className="mt-2 font-sans text-2xl leading-tight text-ink-50 sm:text-4xl">
            {quest.data.title}
          </h1>

          {/* Точки-разделители на телефоне повисали в конце строки: подписи
              переносятся, а разделитель остаётся у предыдущей. Там их нет,
              строки разделяет сам перенос. */}
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-ink-400 sm:gap-x-3 sm:text-sm">
            {/* Кадров, а не раундов: длину партии игрок выбирает сам, и
                квест из трёхсот кадров не означает трёхсот раундов. */}
            <span>{t("quest.photos", { count: rounds })}</span>
            <span className="hidden text-ink-700 sm:inline">•</span>
            <span>{t("quest.views", { count: Number(quest.data.viewCount ?? 0) })}</span>
            {quest.data.author?.username && (
              <>
                <span className="hidden text-ink-700 sm:inline">•</span>
                <span>
                  {t("quest.by")}{" "}
                  <PlayerLink
                    userId={quest.data.author.id}
                    username={quest.data.author.username}
                    className="underline decoration-ink-700 underline-offset-2"
                  />
                </span>
              </>
            )}
            {quest.data.createdAt && (
              <>
                <span className="hidden text-ink-700 sm:inline">•</span>
                <span>{formatDate(quest.data.createdAt)}</span>
              </>
            )}
          </div>
        </div>

        {/* Оценка квеста. Свои звёзды показываются на месте общих, а не
            рядом: одна и та же шкала не должна раздваиваться на экране. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <StarRating
            value={quest.data.canRate ? (quest.data.myRating ?? 0) : (quest.data.rating ?? 0)}
            size={22}
            onRate={
              quest.data.canRate
                ? (rating) => rate.mutate({ questId: questId!, rating })
                : undefined
            }
          />
          <span className="text-sm text-ink-400">
            {quest.data.ratingCount
              ? t("questPage.ratingSummary", {
                  rating: (quest.data.rating ?? 0).toFixed(1),
                  count: quest.data.ratingCount,
                })
              : t("quest.notRated")}
          </span>
          {questId && (
            <FavoriteButton questId={questId} active={Boolean(quest.data.isFavorite)} size={20} />
          )}
        </div>

        {/* Оценивает только тот, кто прошёл: иначе звёзды говорят о впечатлении
            от обложки. Об этом честнее сказать, чем молча не показывать. */}
        {status === "authenticated" && !quest.data.canRate && (
          <p className="text-xs text-ink-500">{t("questPage.rateAfterPlaying")}</p>
        )}

        {quest.data.description && (
          <p className="text-sm leading-relaxed text-ink-300 sm:text-base">{quest.data.description}</p>
        )}

        {quest.data.tags && quest.data.tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {quest.data.tags.map((slug) => (
              <Tag key={slug}>{tagName(tagsIndex.get(slug), slug)}</Tag>
            ))}
          </div>
        )}

        {quest.data.moderationComment && (
          <div className="panel border-amber-400/30 p-4">
            <span className="label">{t("questPage.moderatorNote")}</span>
            <p className="text-sm text-ink-200">{quest.data.moderationComment}</p>
          </div>
        )}

      </div>

      <aside className="space-y-4 sm:space-y-5">
        <div className="panel space-y-3.5 p-4 sm:space-y-4 sm:p-5">
          <h2 className="font-sans text-lg text-ink-50 sm:text-xl">{t("questPage.startTitle")}</h2>

          {/* Что за раунды внутри — видно до начала игры: попасть в область
              и в период это другая задача, чем назвать точку и год, и
              узнавать об этом в середине партии поздно. */}
          {(quest.data.hasAnswerAreas || quest.data.hasYearRanges) && (
            <div className="space-y-2 rounded-xl border border-amber-400/25 bg-amber-400/5 p-3">
              <span className="label mb-0">{t("questPage.whatToExpect")}</span>
              <ul className="space-y-1.5 text-xs text-ink-200">
                {quest.data.hasAnswerAreas && (
                  <li className="flex gap-2">
                    <span aria-hidden className="text-amber-300">◆</span>
                    {t("questPage.hasAreas")}
                  </li>
                )}
                {quest.data.hasYearRanges && (
                  <li className="flex gap-2">
                    <span aria-hidden className="text-amber-300">◆</span>
                    {t("questPage.hasYearRanges")}
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Длина партии и время на раунд — два ползунка подряд, под ними
              две кнопки. Квест — это коллекция, и играют из неё выборку:
              сидеть за квестом на триста кадров никто не станет. */}
          <Slider
            id="round-count"
            label={t("questPage.roundsLabel")}
            value={playRounds}
            min={1}
            max={maxRounds}
            step={1}
            display={t("quest.rounds", { count: playRounds })}
            onChange={setRoundCount}
            onSound={clickSlider}
          />

          {/* Левый край — игра без ограничения по времени. Это не «ноль
              секунд», а отсутствие таймера, поэтому подпись словами. */}
          <Slider
            id="round-seconds"
            label={t("questPage.timerLabel")}
            value={roundSeconds}
            min={0}
            max={maxSeconds}
            step={30}
            display={roundSeconds === 0 ? t("questPage.noTimer") : formatClock(roundSeconds)}
            onChange={setRoundSeconds}
            onSound={clickSlider}
          />

          <button
            type="button"
            className="btn-primary w-full py-3"
            disabled={createSession.isPending || rounds === 0}
            onClick={startSolo}
          >
            {t("questPage.solo")}
          </button>

          <div>
            <button
              type="button"
              className="btn-secondary w-full py-3"
              disabled={startRoomGame.isPending || rounds === 0 || !isRoomHost}
              onClick={() => {
                if (!room?.id) return;
                play("start");
                startRoomGame.mutate(
                  {
                    roomId: room.id,
                    questId: questId!,
                    roundDurationSeconds: roundSeconds,
                    roundCount: playRounds,
                  },
                  { onSuccess: (session) => navigate(`/play/${session.id}`) },
                );
              }}
            >
              {startRoomGame.isPending ? t("room.starting") : t("room.playWithRoom")}
            </button>
            <p className="mt-2 text-xs text-ink-500">
              {!room
                ? t("questPage.inviteHint")
                : isRoomHost
                  ? t("questPage.roomHint", { count: room.members?.length ?? 0 })
                  : t("room.onlyHost")}
            </p>

            {startRoomGame.isError && (
              <p className="mt-2 text-sm text-rust">
                {startRoomGame.error instanceof Error
                  ? startRoomGame.error.message
                  : t("questPage.createFailed")}
              </p>
            )}
          </div>

          {createSession.isError && (
            <p className="text-sm text-rust">
              {createSession.error instanceof Error
                ? createSession.error.message
                : t("questPage.createFailed")}
            </p>
          )}
        </div>

        {/* Пожаловаться на квест можно и не играя: битую обложку или чужие
            фотографии видно с этой страницы. */}
        <button
          type="button"
          className="btn-ghost w-full text-xs text-ink-400"
          onClick={() => setReporting(true)}
        >
          {t("feedback.questProblem")}
        </button>

        {reporting && (
          <FeedbackDialog
            title={t("feedback.questProblem")}
            kind="FEEDBACK_KIND_QUEST"
            lockKind
            questId={questId}
            onClose={() => setReporting(false)}
          />
        )}

        <div className="panel p-4 sm:p-5">
          <h2 className="font-sans text-lg text-ink-50 sm:text-xl">{t("questPage.leaderboard")}</h2>

          {/* Гостю лидерборд не отдаётся — вместо пустого места зовём войти. */}
          {status !== "authenticated" && (
            <p className="mt-3 text-sm text-ink-500">
              {t("questPage.leaderboardLogin")}{" "}
              <Link to="/login" state={{ from: `/quests/${questId}` }} className="text-amber-300 underline underline-offset-2 hover:text-amber-200">
                {t("nav.login")}
              </Link>
            </p>
          )}

          {status === "authenticated" && leaderboard.isPending && <Skeleton className="mt-3 h-40" />}

          {leaderboard.data?.entries?.length === 0 && (
            <p className="mt-3 text-sm text-ink-500">
              {t("questPage.noResults")}
            </p>
          )}

          {leaderboard.data && (leaderboard.data.entries?.length ?? 0) > 0 && (
            <ol className="mt-3 space-y-1.5">
              {leaderboard.data.entries?.slice(0, 10).map((entry) => (
                <li
                  key={`${entry.user?.id}-${entry.rank}`}
                  className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-sm
                             odd:bg-ink-850/50"
                >
                  <span className="flex items-center gap-2.5">
                    <span className="w-5 text-right tabular-nums text-xs text-ink-500">
                      {entry.rank}
                    </span>
                    <Link
                      to={`/players/${entry.user?.id}`}
                      className="text-ink-200 hover:text-amber-300"
                    >
                      {entry.user?.username}
                    </Link>
                  </span>
                  <span className="tabular-nums text-amber-300">
                    {formatScore(entry.totalScore ?? 0)}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </aside>
    </div>
  );
}

/**
 * Ползунок с подписью и текущим значением.
 *
 * Число раундов и время на раунд выбираются одинаково, и выглядеть они должны
 * тоже одинаково: это две настройки одной и той же будущей партии, а не два
 * разных элемента управления.
 */
function Slider({
  id,
  label,
  value,
  min,
  max,
  step,
  display,
  onChange,
  onSound,
}: {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  display: string;
  onChange: (value: number) => void;
  onSound?: () => void;
}) {
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <label className="label mb-0" htmlFor={id}>
          {label}
        </label>
        <span className="text-sm tabular-nums text-amber-300">{display}</span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => {
          onSound?.();
          onChange(Number(event.target.value));
        }}
        // Толще на телефоне: в полоску в полтора пикселя пальцем не попасть.
        className="h-3 w-full cursor-pointer appearance-none rounded-full bg-ink-700
                   accent-amber-400 lg:h-1.5"
      />
    </div>
  );
}
